import React from 'react';
import {
  Alert,
  Box,
  Card,
  CardContent,
  Chip,
  Divider,
  Grid,
  LinearProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography
} from '@mui/material';
import { Link } from 'react-router-dom';
import { AccessTime as AccessTimeIcon, ChatBubbleOutline as ChatBubbleIcon } from '@mui/icons-material';
import { MESSAGE_DISPLAY_LIMIT } from '../../types/common';
import {
  FAMILY_RELATIONSHIP_TYPES,
  getCancerHistoriesArray,
  getCancerTypeName,
  Patient,
  PATIENT_THEME,
  ROLE_COLORS as PATIENT_ROLE_COLORS,
  ROLE_TYPES as PATIENT_ROLE_TYPES
} from '../../types/patient';
import { Nurse, NURSE_TYPE_THEME } from '../../types/nurse';
import { TALK_STATUS_TYPES } from '../../types/talk';
import { ChatMessageDownloadButton, ExternalLinkButton, GenderAgeDisplay, NameNicknameDisplay } from '../common';
import { formatDateWithTime, getHumanReadableTimeAgo } from '../../utils/dateUtils';
import { getExternalTalkRoomUrl } from '../../utils/urlUtils';

interface RelatedPatientsSectionProps {
  nurse: Nurse;
  relatedPatients: Patient[];
  loadingPatients: boolean;
  patientsError: string | null;
}

const RelatedPatientsSection: React.FC<RelatedPatientsSectionProps> = ({
  nurse,
  relatedPatients,
  loadingPatients,
  patientsError
}) => {
  return (
    <Card
      sx={{
        mb: 4,
        border: `1px solid ${NURSE_TYPE_THEME.COMMON.BORDER}40`,
        boxShadow: `0 1px 4px ${NURSE_TYPE_THEME.COMMON.BACKGROUND}30`
      }}>
      <CardContent>
        <Grid container>
          <Grid item xs={12}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <Typography variant="h6">対話したことがある患者</Typography>
              {relatedPatients.length >= 5 && (
                <Chip
                  label={`${relatedPatients.length}人`}
                  size="small"
                  variant="outlined"
                  sx={{
                    ml: 1,
                    fontWeight: 'bold',
                    color: PATIENT_THEME.LIST.DARK,
                    borderColor: PATIENT_THEME.LIST.DARK,
                    borderWidth: 1.5
                  }}
                />
              )}
            </Box>
            <Divider sx={{ mb: 2 }}/>

            {loadingPatients && (
              <Box sx={{ width: '100%', mt: 1, mb: 1 }}>
                <LinearProgress/>
              </Box>
            )}

            {patientsError && (
              <Alert severity="error" sx={{ mb: 2 }}>{patientsError}</Alert>
            )}

            {!loadingPatients && !patientsError && relatedPatients.length === 0 && (
              <Box sx={{ p: 2 }}>
                <Typography
                  variant="body1"
                  sx={{ color: 'text.disabled', fontStyle: 'italic' }}
                >
                  対話歴のある患者はいません
                </Typography>
              </Box>
            )}

            {!loadingPatients && !patientsError && relatedPatients.length > 0 && (
              <Box
                sx={{
                  mt: 2,
                  position: 'relative',
                  '&::after': {
                    content: '""',
                    position: 'absolute',
                    bottom: 0,
                    left: 0,
                    right: 0,
                    height: '20px',
                    background: `linear-gradient(to top, ${PATIENT_THEME.LIST.BACKGROUND}CC, ${PATIENT_THEME.LIST.BACKGROUND}00)`,
                    pointerEvents: 'none',
                    zIndex: 1,
                    display: 'block'
                  }
                }}
              >
                <TableContainer
                  component={Paper}
                  sx={{
                    borderRadius: 1,
                    maxHeight: '500px', // 最大高さを指定
                    overflow: 'auto', // スクロール可能に
                    '&::-webkit-scrollbar': {
                      width: '8px',
                      backgroundColor: `${PATIENT_THEME.LIST.BACKGROUND}`,
                    },
                    '&::-webkit-scrollbar-thumb': {
                      backgroundColor: `${PATIENT_THEME.LIST.BORDER}`,
                      borderRadius: '4px',
                      '&:hover': {
                        backgroundColor: `${PATIENT_THEME.LIST.DARK}`,
                      }
                    }
                  }}
                >
                  <Table
                    stickyHeader
                    aria-label="対話歴のある患者一覧"
                    sx={{
                      "& .MuiTableHead-root": {
                        position: "sticky",
                        top: 0,
                        zIndex: 2
                      },
                      "& .MuiTableHead-root .MuiTableCell-root": {
                        backgroundColor: `${PATIENT_THEME.LIST.BACKGROUND} !important`,
                        borderBottom: `2px solid ${PATIENT_THEME.LIST.BORDER}`
                      }
                    }}
                  >
                    <TableHead>
                      <TableRow>
                        <TableCell width="10%" sx={{ fontWeight: 'medium', color: 'text.secondary' }}>
                          ロール
                        </TableCell>
                        <TableCell width="25%" sx={{ fontWeight: 'medium', color: 'text.secondary' }}>
                          名前 / ニックネーム
                        </TableCell>
                        <TableCell width="10%" sx={{ fontWeight: 'medium', color: 'text.secondary' }}>
                          性別 (年齢)
                        </TableCell>
                        <TableCell width="15%" sx={{ fontWeight: 'medium', color: 'text.secondary' }}>
                          がん疾患歴
                        </TableCell>
                        <TableCell width="27%" sx={{ fontWeight: 'medium', color: 'text.secondary' }}>
                          最終メッセージ / 日時
                        </TableCell>
                        <TableCell width="13%" sx={{ fontWeight: 'medium', color: 'text.secondary' }}>
                          対話状態 / チャット
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {relatedPatients
                      .slice() // 元の配列を変更しないようにコピー
                      .sort((a, b) => {
                        // latest_talk_last_chat_send_atがない場合は後ろに配置
                        if (!a.latest_talk_last_chat_send_at) return 1;
                        if (!b.latest_talk_last_chat_send_at) return -1;
                        // 降順（新しい順）に並べる
                        return new Date(b.latest_talk_last_chat_send_at).getTime() - new Date(a.latest_talk_last_chat_send_at).getTime();
                      })
                      .map((patient) => (
                        <TableRow
                          key={patient.id}
                          hover
                          component={Link}
                          to={`/talk_rooms/summary?nurse_id=${nurse.id}&patient_id=${patient.id}`}
                          sx={{
                            textDecoration: 'none',
                            color: 'inherit',
                            '&:hover': {
                              backgroundColor: 'rgba(0, 0, 0, 0.04)',
                            }
                          }}
                        >
                          {/* ロール */}
                          <TableCell>
                            <Typography
                              variant="body2"
                              sx={{
                                color: PATIENT_ROLE_COLORS[patient.role_type as keyof typeof PATIENT_ROLE_COLORS] || '#757575',
                                fontSize: '0.75rem',
                                borderLeft: `3px solid ${PATIENT_ROLE_COLORS[patient.role_type as keyof typeof PATIENT_ROLE_COLORS] || '#757575'}`,
                                pl: 1,
                                py: 0.3,
                                opacity: 0.85,
                                whiteSpace: 'nowrap'
                              }}
                            >
                              {patient.role_type === 'FAMILY' && patient.family_relationship_type
                                ? (FAMILY_RELATIONSHIP_TYPES[patient.family_relationship_type as keyof typeof FAMILY_RELATIONSHIP_TYPES] || patient.family_relationship_type)
                                : PATIENT_ROLE_TYPES[patient.role_type as keyof typeof PATIENT_ROLE_TYPES]
                              }
                            </Typography>
                          </TableCell>

                          {/* 患者名とニックネーム */}
                          <TableCell>
                            <NameNicknameDisplay
                              name={patient.name}
                              nickname={patient.nickname}
                              nameFontSize="1rem"
                              nameFontWeight="normal"
                              nicknameValueColor="#000000"
                              nicknameValueFontWeight="bold"
                              isTestAccount={patient.is_test_account}
                            />
                          </TableCell>

                          {/* 性別・年齢 */}
                          <TableCell>
                            <GenderAgeDisplay
                              genderType={patient.gender_type}
                              birthday={patient.birthday}
                              fontSize="0.9rem"
                            />
                          </TableCell>

                          {/* がん疾患歴 */}
                          <TableCell>
                            {(() => {
                              try {
                                const histories = getCancerHistoriesArray(patient.cancer_histories);
                                if (!histories || histories.length === 0) {
                                  return (
                                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.85rem' }}>
                                      がん疾患なし
                                    </Typography>
                                  );
                                }
                                // 各がん種類をマッピングして取得
                                const cancerTypes = histories
                                .filter(h => h && typeof h === 'object')
                                .map(h => {
                                  const cancerType = h.cancer_type || '';
                                  let displayName = '';
                                  // 'OTHER'の場合はotherフィールドを使用
                                  if (cancerType === 'OTHER' && h.other) {
                                    displayName = h.other.length > 7 ?
                                      `${h.other.substring(0, 7)}...` : h.other;
                                  } else {
                                    displayName = getCancerTypeName(cancerType);
                                  }
                                  // 診断日の処理
                                  let diagnosisDate = '(不明)';
                                  if (h.year || h.month) {
                                    const year = h.year || '??';
                                    const month = h.month || '??';
                                    diagnosisDate = `(${year}/${month})`;
                                  }
                                  return `${displayName} ${diagnosisDate}`;
                                })
                                .filter(Boolean);
                                if (cancerTypes.length > 0) {
                                  return (
                                    <Typography
                                      variant="body2"
                                      sx={{
                                        fontSize: '0.85rem',
                                        fontWeight: 'medium'
                                      }}
                                    >
                                      {cancerTypes.join('、')}
                                    </Typography>
                                  );
                                }
                                return (
                                  <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.85rem' }}>
                                    がん疾患なし
                                  </Typography>
                                );
                              } catch (e) {
                                return (
                                  <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.85rem' }}>
                                    がん疾患なし
                                  </Typography>
                                );
                              }
                            })()}
                          </TableCell>

                          {/* 最終メッセージ内容と送信日時 */}
                          <TableCell>
                            {patient.latest_talk_last_chat_message ? (
                              patient.latest_talk_last_chat_message.length > MESSAGE_DISPLAY_LIMIT ? (
                                // 制限文字数を超える場合はTooltipを表示
                                <Tooltip
                                  title={patient.latest_talk_last_chat_message}
                                  arrow
                                  placement="top"
                                  enterDelay={500}
                                  sx={{ maxWidth: 500 }}
                                >
                                  <Typography
                                    variant="body2"
                                    sx={{
                                      fontSize: '0.85rem',
                                      color: 'text.primary',
                                      cursor: 'help'
                                    }}
                                  >
                                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                      {`${patient.latest_talk_last_chat_message.substring(0, MESSAGE_DISPLAY_LIMIT)}...`}
                                      <ChatBubbleIcon sx={{ ml: 0.5, fontSize: '0.9rem', color: 'text.secondary', opacity: 0.7 }}/>
                                    </Box>
                                  </Typography>
                                </Tooltip>
                              ) : (
                                // 20文字以内の場合はTooltipなし
                                <Typography
                                  variant="body2"
                                  sx={{
                                    fontSize: '0.85rem',
                                    color: 'text.primary'
                                  }}
                                >
                                  {patient.latest_talk_last_chat_message}
                                </Typography>
                              )
                            ) : (
                              <Typography variant="body2" sx={{ color: 'text.secondary', fontStyle: 'italic', fontSize: '0.85rem' }}>
                                メッセージなし
                              </Typography>
                            )}

                            {/* 最終チャット送信日時 */}
                            {patient.latest_talk_last_chat_send_at && (
                              <Tooltip
                                title={`最終送信日時: ${formatDateWithTime(patient.latest_talk_last_chat_send_at)}`}
                                arrow
                                placement="left"
                              >
                                <Box sx={{ display: 'flex', alignItems: 'center', cursor: 'help', mt: 0.5 }}>
                                  <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                    最終送信: {getHumanReadableTimeAgo(patient.latest_talk_last_chat_send_at)}
                                  </Typography>
                                  <AccessTimeIcon fontSize="small" color="action" sx={{ fontSize: '0.8rem', opacity: 0.7 }}/>
                                </Box>
                              </Tooltip>
                            )}
                          </TableCell>

                          {/* 対話ステータスとチャットリンク */}
                          <TableCell>
                            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                              {/* 対話ステータス */}
                              {patient.latest_talk_status && (
                                <Chip
                                  label={
                                    (patient.latest_talk_status === 'DONE' && patient.latest_talk_is_force_end)
                                      ? '強制終了'
                                      : (TALK_STATUS_TYPES[patient.latest_talk_status as keyof typeof TALK_STATUS_TYPES] || patient.latest_talk_status)
                                  }
                                  size="small"
                                  sx={{
                                    fontSize: '0.75rem',
                                    alignSelf: 'flex-start',
                                    mb: 0.5,
                                    fontWeight: (patient.latest_talk_status === 'DONE' && patient.latest_talk_is_force_end) ? 'bold' : 'normal'
                                  }}
                                  color={
                                    patient.latest_talk_status === 'DOING'
                                      ? 'success'
                                      : 'default'
                                  }
                                />
                              )}

                              {/* チャットリンク */}
                              {patient.latest_talk_room_id && (
                                <Box sx={{ mt: 0.5, display: 'flex', gap: 1.5 }}>
                                  <ExternalLinkButton
                                    href={getExternalTalkRoomUrl(patient.latest_talk_room_id)}
                                    label="開く"
                                    size="small"
                                    buttonType="chat"
                                    themeColor={{
                                      main: PATIENT_THEME.LIST.DARK,
                                      hover: PATIENT_THEME.LIST.BACKGROUND
                                    }}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                    }}
                                  />
                                  <div onClick={(e) => { e.preventDefault(); e.stopPropagation(); }}>
                                    <ChatMessageDownloadButton
                                      roomType="TALK_ROOM"
                                      roomId={patient.latest_talk_room_id}
                                      size="small"
                                      variant="outlined"
                                      iconOnly={true}
                                      themeColor={{
                                        main: PATIENT_THEME.LIST.DARK,
                                        hover: PATIENT_THEME.LIST.BACKGROUND
                                      }}
                                    />
                                  </div>
                                </Box>
                              )}
                            </Box>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            )}
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
};
export default RelatedPatientsSection;
